import React, { useEffect, useMemo, useRef, useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DescriptionIcon from "@mui/icons-material/Description";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import ImageIcon from "@mui/icons-material/Image";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
// @ts-ignore 워커 엔트리에는 타입 선언이 없다. webpack 이 별도 청크로 뽑아준다.
import pdfWorker from "pdfjs-dist/legacy/build/pdf.worker.entry";
import {
  getAttachFileBlob,
  getAttachFilePreviewBlob,
} from "./SN_SYS_BAS_REP_001_api";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

interface previewProps {
  row: any;
  // form: 프리폼(SharedForm) 그리드 흐름에 맞춰 행/열을 차지한다.
  // fill: 미리보기 팝업처럼 부모 영역을 그대로 채운다.
  variant?: "form" | "fill";
}

// 행을 클릭할 때마다 파일을 받으므로, 이보다 큰 파일은 받지 않고 아이콘만 보여준다.
const MAX_PREVIEW_BYTES = 10 * 1024 * 1024;
// 텍스트는 앞부분만 보여준다. (로그 파일 등이 통째로 렌더되는 것을 막는다)
const MAX_TEXT_LENGTH = 50000;

// SharedForm 그리드( gridAutoRows 32px + rowGap 10px + padding 10px ) 기준 값.
const FORM_ROW_HEIGHT = 32;
const FORM_ROW_GAP = 10;
const FORM_PADDING_BOTTOM = 10;
const MIN_BOX_HEIGHT = 120;

// 시트가 크면 화면이 멈추므로 앞부분만 표로 그린다.
const MAX_SHEET_ROWS = 200;
const MAX_SHEET_COLS = 30;

// PDF 는 브라우저 내장 뷰어에 맡기지 않고 직접 canvas 에 그린다.
// 내장 뷰어는 툴바와 어두운 배경을 스스로 그려서 이미지·엑셀 미리보기와 모양이 달라진다.
const MAX_PDF_PAGES = 20;

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];
const PDF_EXTS = ["pdf"];
const TEXT_EXTS = ["txt", "csv", "log", "json", "xml", "md", "yml", "yaml"];
const EXCEL_EXTS = ["xls", "xlsx", "xlsm", "xlsb"];
const WORD_EXTS = ["doc", "docx", "docm", "rtf"];
const PPT_EXTS = ["ppt", "pptx", "pptm", "pps", "ppsx"];
const ARCHIVE_EXTS = ["zip", "rar", "7z", "alz", "egg", "tar", "gz"];
// 브라우저에서 못 그리는 형식. 서버(/snotes/file/preview)가 PDF 로 변환해 준다.
const SERVER_CONVERT_EXTS = [
  "ppt", "pptx", "pptm", "pps", "ppsx",
  "doc", "rtf",
  "hwp", "hwpx",
  "odt", "ods", "odp",
];

type PreviewKind =
  | "IMAGE"
  | "PDF"
  | "TEXT"
  | "EXCEL"
  | "WORD"
  | "SERVER_PDF"
  | "NONE";

// 확장자는 nm_file_ext 컬럼을 우선 쓰고, 비어 있으면 파일명에서 잘라낸다.
const getExt = (row: any): string => {
  const ext = String(row?.nm_file_ext || "").trim();
  if (ext !== "") return ext.replace(/^\./, "").toLowerCase();

  const name = String(row?.nm_file || "");
  const idx = name.lastIndexOf(".");
  return idx < 0 ? "" : name.slice(idx + 1).toLowerCase();
};

// 서버 변환 API 의 상태코드별 안내. 사유가 다르면 사용자가 할 수 있는 일도 다르다.
const getFailMessage = (status?: number): string => {
  switch (status) {
    case 403:
      return "미리보기 권한이 없습니다.";
    case 404:
      return "원본 파일을 찾을 수 없습니다.";
    case 413:
      return "파일이 커서 미리보기를 생략했습니다. 다운로드 후 확인해 주세요.";
    case 415:
      return "미리보기를 지원하지 않는 형식입니다.";
    case 504:
      return "변환이 지연되어 미리보기를 불러오지 못했습니다.";
    default:
      return "미리보기를 불러오지 못했습니다.";
  }
};

const getPreviewKind = (ext: string): PreviewKind => {
  if (IMAGE_EXTS.includes(ext)) return "IMAGE";
  if (PDF_EXTS.includes(ext)) return "PDF";
  if (TEXT_EXTS.includes(ext)) return "TEXT";
  if (EXCEL_EXTS.includes(ext)) return "EXCEL";
  if (ext === "docx" || ext === "docm") return "WORD";
  if (SERVER_CONVERT_EXTS.includes(ext)) return "SERVER_PDF";
  return "NONE";
};

// 미리보기가 안 되는 형식은 확장자 아이콘으로 대체한다.
const getFileIcon = (ext: string) => {
  const size = { fontSize: 48 };
  if (PDF_EXTS.includes(ext))
    return <PictureAsPdfIcon sx={{ ...size, color: "#d32f2f" }} />;
  if (EXCEL_EXTS.includes(ext))
    return <TableChartIcon sx={{ ...size, color: "#1d6f42" }} />;
  if (WORD_EXTS.includes(ext))
    return <DescriptionIcon sx={{ ...size, color: "#2b579a" }} />;
  if (PPT_EXTS.includes(ext))
    return <SlideshowIcon sx={{ ...size, color: "#d24726" }} />;
  if (IMAGE_EXTS.includes(ext))
    return <ImageIcon sx={{ ...size, color: "#0288d1" }} />;
  if (ARCHIVE_EXTS.includes(ext))
    return <FolderZipIcon sx={{ ...size, color: "#f9a825" }} />;
  return <InsertDriveFileIcon sx={{ ...size, color: "#757575" }} />;
};

/**
 * 우측 상세 패널의 파일 내용 미리보기.
 * 이미지 / PDF / 텍스트만 실제 내용을 보여주고, 나머지는 아이콘과 안내로 대체한다.
 */
const SN_SYS_BAS_REP_001_PREVIEW = ({
  row,
  variant = "form",
}: previewProps) => {
  const [objectUrl, setObjectUrl] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [sheetRows, setSheetRows] = useState<any[][]>([]);
  const [sheetName, setSheetName] = useState<string>("");
  const [wordHtml, setWordHtml] = useState<string>("");
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const pdfBoxRef = useRef<HTMLDivElement>(null);
  const [message, setMessage] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState<number>(MIN_BOX_HEIGHT);
  const [colSpan, setColSpan] = useState<number>(1);

  const ext = useMemo(() => getExt(row), [row]);
  const kind = useMemo(() => getPreviewKind(ext), [ext]);
  const idFile = row?.id;
  const nmFile = String(row?.nm_file || "");
  const qtSize = Number(row?.qt_size);

  useEffect(() => {
    let createdUrl = "";
    let canceled = false;

    const clear = () => {
      setObjectUrl("");
      setText("");
      setSheetRows([]);
      setSheetName("");
      setWordHtml("");
      setPdfData(null);
    };

    const load = async () => {
      clear();
      setMessage("");

      if (!row || !idFile) {
        setMessage("행을 선택하면 파일 내용을 보여줍니다.");
        return;
      }
      if (kind === "NONE") {
        // PPT 와 구형 이진 포맷(.doc/.xls 이전 버전)은 브라우저에서 그릴 수 없다.
        setMessage(
          PPT_EXTS.includes(ext)
            ? "PPT 는 미리보기를 지원하지 않습니다. 다운로드 후 확인해 주세요."
            : "미리보기를 지원하지 않는 형식입니다."
        );
        return;
      }
      if (
        kind !== "SERVER_PDF" &&
        !Number.isNaN(qtSize) &&
        qtSize > MAX_PREVIEW_BYTES
      ) {
        setMessage("파일이 커서 미리보기를 생략했습니다.");
        return;
      }

      setIsLoading(true);
      try {
        if (kind === "SERVER_PDF") {
          // 서버가 LibreOffice 로 변환한 PDF 만 받는다. 원본은 받지 않는다.
          const pdfBlob = await getAttachFilePreviewBlob(row);
          if (canceled) return;

          const converted = await pdfBlob.arrayBuffer();
          if (canceled) return;

          setPdfData(converted);
          return;
        }

        const blob = await getAttachFileBlob(row);
        if (canceled) return;

        if (kind === "TEXT") {
          const content = await blob.text();
          if (canceled) return;
          setText(content.slice(0, MAX_TEXT_LENGTH));
        } else if (kind === "WORD") {
          // docx 도 zip + XML 이라 브라우저가 못 그린다. mammoth 로 HTML 로 바꾼다.
          const buffer = await blob.arrayBuffer();
          if (canceled) return;

          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          if (canceled) return;

          const html = String(result?.value || "").trim();
          if (html === "") {
            setMessage("내용이 없는 문서입니다.");
            return;
          }
          setWordHtml(html);
        } else if (kind === "EXCEL") {
          // xlsx 는 zip + XML 이라 브라우저가 못 그린다. SheetJS 로 파싱해 표로 만든다.
          const buffer = await blob.arrayBuffer();
          if (canceled) return;

          const book = XLSX.read(buffer, { type: "array" });
          const firstSheet = book.SheetNames[0];
          if (!firstSheet) {
            setMessage("시트가 없는 파일입니다.");
            return;
          }

          const parsed: any[][] = XLSX.utils.sheet_to_json(
            book.Sheets[firstSheet],
            { header: 1, blankrows: false, defval: "" }
          );
          setSheetName(firstSheet);
          setSheetRows(
            parsed
              .slice(0, MAX_SHEET_ROWS)
              .map((row) => row.slice(0, MAX_SHEET_COLS))
          );
        } else if (kind === "PDF") {
          const buffer = await blob.arrayBuffer();
          if (canceled) return;
          setPdfData(buffer);
        } else {
          createdUrl = window.URL.createObjectURL(blob);
          setObjectUrl(createdUrl);
        }
      } catch (error: any) {
        // 미리보기 실패는 안내 문구로 대체한다. (다운로드는 그대로 쓸 수 있다)
        console.error("미리보기를 실패했습니다.", error);
        if (!canceled) setMessage(getFailMessage(error?.response?.status));
      } finally {
        if (!canceled) setIsLoading(false);
      }
    };

    load();

    // 행을 빠르게 옮겨 다닐 때 이전 요청 결과가 뒤늦게 반영되지 않도록 막는다.
    return () => {
      canceled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [row, idFile, kind, qtSize]);

  // 프리폼(SharedForm)의 그리드 흐름 안에서 마지막 항목 바로 아래 줄에
  // 전체 폭으로 놓인다. gridAutoRows 가 32px 이라 필요한 만큼 행을 차지시킨다.
  // 받은 PDF 를 페이지마다 canvas 로 그린다. 브라우저 내장 뷰어를 쓰지 않으므로
  // 툴바도 어두운 배경도 없고, 이미지 미리보기와 같은 모양이 된다.
  useEffect(() => {
    const container = pdfBoxRef.current;
    if (!pdfData || !container) return;

    let canceled = false;
    let task: any = null;

    const draw = async () => {
      container.innerHTML = "";

      // pdf.js 가 넘긴 버퍼를 가져가 버리므로 복사본을 준다.
      task = pdfjsLib.getDocument({ data: pdfData.slice(0) });
      const doc = await task.promise;
      if (canceled) return;

      const pageCount = Math.min(doc.numPages, MAX_PDF_PAGES);
      const ratio = window.devicePixelRatio || 1;

      for (let no = 1; no <= pageCount; no++) {
        const page = await doc.getPage(no);
        if (canceled) return;

        const base = page.getViewport({ scale: 1 });
        const width = container.clientWidth || base.width;
        const viewport = page.getViewport({ scale: (width / base.width) * ratio });

        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = "100%";
        canvas.style.display = "block";
        canvas.style.marginBottom = no < pageCount ? "6px" : "0";

        const ctx = canvas.getContext("2d");
        if (!ctx) continue;

        container.appendChild(canvas);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (canceled) return;
      }
    };

    draw().catch((error) => {
      console.error("PDF 를 그리지 못했습니다.", error);
    });

    return () => {
      canceled = true;
      try {
        task?.destroy?.();
      } catch (error) {
        // 이미 정리된 경우는 무시한다.
      }
    };
  }, [pdfData]);

  // 프리폼에서 항목들이 차지하고 남은 세로 공간을 미리보기가 모두 채우게 한다.
  // 패널 폭을 조절하면 항목이 줄바꿈되어 높이가 달라지므로 그때마다 다시 잰다.
  useEffect(() => {
    // 팝업(fill)은 부모 영역을 그대로 채우므로 그리드 기준으로 재지 않는다.
    if (variant === "fill") return;

    const el = boxRef.current;
    const container = el?.parentElement;
    if (!el || !container) return;

    const fit = () => {
      // SharedForm 그리드는 gridTemplateColumns 없이 컨트롤의 auto/span 으로만
      // 열이 만들어지는 암시적 그리드다. 그래서 "1 / -1" 로는 한 칸만 차지한다.
      // 실제로 만들어진 열 수를 읽어 그만큼 span 시켜야 가로로 꽉 찬다.
      const tracks = window
        .getComputedStyle(container)
        .gridTemplateColumns.split(" ")
        .filter((track) => track.trim() !== "" && track !== "none");
      setColSpan(Math.max(1, tracks.length));

      const available =
        container.clientHeight - el.offsetTop - FORM_PADDING_BOTTOM;
      setBoxHeight(Math.max(MIN_BOX_HEIGHT, available));
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [variant]);

  // 그리드 행 높이가 32px 로 고정이라, 잰 높이만큼 행을 차지하도록 환산한다.
  const rowSpan = Math.max(
    1,
    Math.round((boxHeight + FORM_ROW_GAP) / (FORM_ROW_HEIGHT + FORM_ROW_GAP))
  );

  const boxStyle: React.CSSProperties =
    variant === "fill"
      ? {
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          border: "1px solid #babfc7",
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: "#fff",
        }
      : {
          gridColumn: `1 / span ${colSpan}`,
          gridRow: `span ${rowSpan}`,
          height: boxHeight,
          border: "1px solid #babfc7",
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: "#fff",
        };

  const renderBody = () => {
    if (isLoading) {
      return <span className="text-xs text-gray-500">불러오는 중...</span>;
    }
    if (message) {
      return (
        <div className="flex flex-col items-center gap-1">
          {getFileIcon(ext)}
          <span className="text-xs text-gray-500">{message}</span>
        </div>
      );
    }
    if (kind === "IMAGE" && objectUrl) {
      return (
        <img
          src={objectUrl}
          alt={nmFile}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      );
    }
    if ((kind === "PDF" || kind === "SERVER_PDF") && pdfData) {
      return (
        <div
          ref={pdfBoxRef}
          style={{ width: "100%", height: "100%", overflow: "auto" }}
        />
      );
    }
    if (kind === "WORD" && wordHtml) {
      return (
        <div
          className="text-xs"
          style={{
            width: "100%",
            height: "100%",
            overflow: "auto",
            padding: "8px",
            wordBreak: "break-all",
          }}
          // mammoth 가 만든 문서 본문 HTML. 스크립트는 변환 대상이 아니라 포함되지 않는다.
          dangerouslySetInnerHTML={{ __html: wordHtml }}
        />
      );
    }
    if (kind === "EXCEL" && sheetRows.length > 0) {
      return (
        <div style={{ width: "100%", height: "100%", overflow: "auto" }}>
          <div className="text-xs text-gray-500" style={{ padding: "4px 6px" }}>
            {sheetName}
          </div>
          <table
            className="text-xs"
            style={{ borderCollapse: "collapse", width: "max-content" }}
          >
            <tbody>
              {sheetRows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  {row.map((cell, colIdx) => (
                    <td
                      key={colIdx}
                      style={{
                        border: "1px solid #e0e0e0",
                        padding: "2px 6px",
                        whiteSpace: "nowrap",
                        backgroundColor: rowIdx === 0 ? "#f5f5f5" : undefined,
                      }}
                    >
                      {String(cell ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (kind === "TEXT") {
      return (
        <pre
          className="text-xs"
          style={{
            width: "100%",
            height: "100%",
            margin: 0,
            padding: "8px",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {text}
        </pre>
      );
    }
    return null;
  };

  return (
    <div
      ref={boxRef}
      className="flex items-center justify-center shrink-0"
      style={boxStyle}
    >
      {renderBody()}
    </div>
  );
};

export default React.memo(SN_SYS_BAS_REP_001_PREVIEW);
